import { useState, type JSX } from 'react';
import { ChevronDownIcon, ChevronUpIcon, LinkIcon } from 'lucide-react';
import { toast } from 'sonner';
import type { DocumentStyle } from '@worldathletics/types';
import {
  buildAnnexPath,
  formatContainerNameLabel,
  resolveHeadingTitleSeparator,
  slugify,
} from '@worldathletics/utilities';

import { HtmlContent } from '@/components/atoms/html-content/html-content';
import { useDocumentNumberingDefaults } from '@/components/document/document-preview';
import {
  AnnexLinkProvider,
  useAnnexLink,
  useAnnexParentDefinitionSlug,
} from '@/context/annex-link/annex-link-context';
import { useNodeName } from '@/context/names/names-context';
import { useReferenceMap } from '@/context/references/references-context';
import { resolveRefTokens } from '@/lib/resolve-ref-tokens';
import { sanitizeBlockHtml } from '@/lib/sanitize';
import type { DocumentEntity } from '@/context/store/slices/documentDataSlice';
import { styleRuleToCssVars, useStyleRule } from '@/lib/style-profile';

import './document-view-container.scss';

type Props = {
  node: DocumentEntity & { type: 'container' };
  childNodes?: JSX.Element[];
  depth?: number;
  parentStyle?: string | null;
};

// 'book' is intentionally OMITTED from this set: a book container's name is
// always meaningful as just its label (e.g. "Competition Rules") with no
// computed numbering prefix in front. Including it here would render
// "1 — Competition Rules" which adds noise.
const STYLES_WITH_NAME_PREFIX: ReadonlySet<DocumentStyle> =
  new Set<DocumentStyle>([
    'part',
    'appendix',
    'rule',
    'article',
    'section',
    'definition',
  ]);

function DocumentViewContainer({
  node,
  childNodes,
  depth = 0,
  parentStyle = null,
}: Props) {
  const [visibility, setVisibility] = useState(true);
  const name = useNodeName(node.id);
  const refMap = useReferenceMap();
  const rule = useStyleRule(node.style, depth);
  const isAnnexLink = useAnnexLink();
  const parentDefinitionSlug = useAnnexParentDefinitionSlug();
  const hasChildren = Boolean(childNodes && childNodes.length > 0);
  const showExpand = hasChildren;
  const definitionSlug =
    isAnnexLink && node.style === 'definition' && node.text
      ? slugify(node.text)
      : null;
  const showDefinitionCopyLink = definitionSlug !== null;
  const showChildCopyLink =
    isAnnexLink &&
    parentDefinitionSlug !== null &&
    node.style !== 'definition';
  const showCopyLink = showDefinitionCopyLink || showChildCopyLink;
  const showNamePrefix = STYLES_WITH_NAME_PREFIX.has(node.style);
  const documentDefaults = useDocumentNumberingDefaults();
  const numberingStylePosition =
    node.numberingStylePosition ??
    documentDefaults.numberingStylePosition ??
    'suffix';
  const nameLabel =
    showNamePrefix && name
      ? formatContainerNameLabel({
          autoNumber: name,
          prefix: node.prefix,
          displaySuffix: node.title,
          numberingStylePosition,
          style: node.style,
        })
      : '';
  const headingText = node.text ?? '';
  const headingHtml = sanitizeBlockHtml(
    resolveRefTokens(headingText, refMap, node.id),
  );
  const titleSeparator =
    headingText.trim().length > 0
      ? resolveHeadingTitleSeparator({
          style: node.style,
          parentStyle,
        })
      : '';

  const styleVars = styleRuleToCssVars(rule);

  function handleCopyLink() {
    const hash =
      showChildCopyLink && parentDefinitionSlug
        ? buildAnnexPath(parentDefinitionSlug, node.id).split('#')[1]
        : slugify(node.text ?? '');
    const url = `${window.location.origin}${window.location.pathname}${window.location.search}#${hash ?? ''}`;
    void navigator.clipboard.writeText(url).then(() => {
      toast.success('Link copied');
    });
  }

  return (
    <div
      id={node.id}
      data-type="card"
      data-style={node.style}
      className={`doc-view-container is-${node.style}`}
      style={styleVars}
    >
      <div className="doc-view-container__title">
        {showNamePrefix && name && (
          <span className="doc-view-container__name">
            {nameLabel}
            {titleSeparator}
          </span>
        )}
        <HtmlContent
          as="div"
          role="heading"
          aria-level={2}
          className="doc-view-container__title-text"
          html={headingHtml}
        />
      </div>
      {(showExpand || showCopyLink) && (
        <div className="doc-view-container__toolbar">
          {showCopyLink && (
            <button
              type="button"
              className="doc-view-container__copy-link"
              onClick={handleCopyLink}
              aria-label="Copy link"
            >
              <LinkIcon size={14} />
            </button>
          )}
          {showExpand && (
            <button
              type="button"
              className="doc-view-container__expand-toggle"
              onClick={() => setVisibility(!visibility)}
              aria-label={visibility ? 'Collapse' : 'Expand'}
            >
              {visibility ? (
                <ChevronUpIcon size={14} />
              ) : (
                <ChevronDownIcon size={14} />
              )}
            </button>
          )}
        </div>
      )}
      <div
        className="doc-view-container__content"
        style={visibility ? { display: 'flex' } : { display: 'none' }}
      >
        {definitionSlug ? (
          <AnnexLinkProvider parentDefinitionSlug={definitionSlug}>
            {childNodes}
          </AnnexLinkProvider>
        ) : (
          childNodes
        )}
      </div>
    </div>
  );
}

export { DocumentViewContainer };
